import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import API from "../../api/Api";
import { CUSTOMER_AVATAR_UPDATE } from "../../store/actions/CustomerActions";
import { thunkCreateSuccessNotification, thunkCreateErrorNotification } from "../../store/actions/NotificationActions";
import "./ProfilePicture.css";

interface ProfilePictureProps {
  onAvatarUpdate?: () => void;
}

export const ProfilePicture: React.FC<ProfilePictureProps> = ({ onAvatarUpdate }) => {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    loadCurrentAvatar();
  }, []);

  const loadCurrentAvatar = async () => {
    try {
      const avatarLink = await API.getCustomerAvatar();
      setAvatarUrl(avatarLink);
    } catch (error) {
      // No avatar available - this is normal for users without profile pictures
      setAvatarUrl(null);
    }
  };

  const validateFile = (file: File): boolean => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];

    if (!allowedTypes.includes(file.type)) {
      dispatch(thunkCreateErrorNotification("Nur JPEG, PNG und GIF Dateien sind erlaubt.", "VALIDATION_ERROR"));
      return false;
    }

    if (file.size > maxSize) {
      dispatch(thunkCreateErrorNotification("Die Datei ist zu groß. Maximale Dateigröße: 5MB.", "VALIDATION_ERROR"));
      return false;
    }

    return true;
  };

  const handleFileUpload = async (file: File) => {
    if (!validateFile(file)) {
      return;
    }

    setIsLoading(true);

    try {
      await API.uploadCustomerAvatar(file);

      // Reload the avatar to show the new one
      await loadCurrentAvatar();

      dispatch(thunkCreateSuccessNotification("Profilbild erfolgreich aktualisiert!"));

      // Trigger Redux action to update avatar state
      dispatch({
        type: CUSTOMER_AVATAR_UPDATE,
        payload: true,
      });

      // Notify parent component
      if (onAvatarUpdate) {
        onAvatarUpdate();
      }
    } catch (error) {
      dispatch(thunkCreateErrorNotification("Fehler beim Hochladen. Bitte versuchen Sie es erneut.", error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleButtonClick = () => {
    if (!isLoading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className="profile-picture-container">
      <button
        type="button"
        className={`profile-picture-avatar rounded-circle d-inline-flex align-items-center justify-content-center position-relative ${
          isLoading ? "loading" : ""
        }`}
        style={{
          width: "200px",
          height: "200px",
          backgroundImage: avatarUrl ? `url(${avatarUrl})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
        onClick={handleButtonClick}
        title={isLoading ? "Wird hochgeladen..." : "Klicken um Profilbild zu ändern"}
      >
        {!avatarUrl && !isLoading && <i className="bi-person upload-icon" style={{ fontSize: "60px" }}></i>}
        {isLoading && (
          <div className="loading-overlay position-absolute top-0 start-0 w-100 h-100 rounded-circle d-flex flex-column align-items-center justify-content-center">
            <div className="loading-spinner mb-1">
              <i className="bi-cloud-upload text-primary" style={{ fontSize: "24px" }}></i>
            </div>
            <div className="loading-text">Uploading...</div>
          </div>
        )}
      </button>

      <div className="profile-picture-label">{avatarUrl ? "Profilbild" : "Profilbild hinzufügen"}</div>

      <div className={`profile-picture-hint ${isLoading ? "loading-text" : ""}`}>
        {isLoading && (
          <>
            <i className="bi-arrow-up-circle me-2"></i>
            Bild wird hochgeladen...
          </>
        )}
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        accept="image/*"
        style={{ display: "none" }}
        disabled={isLoading}
      />
    </div>
  );
};

export default ProfilePicture;
